import React from "react";
import { Edit2 } from "lucide-react";
import { cn } from '../../lib/utils';

interface DaySchedule {
  day: string;
  openTime: string;
  closeTime: string;
  allowBooking: boolean;
}

interface BookingScheduleTableProps {
  schedule: DaySchedule[];
  onToggleBooking: (index: number) => void;
  onEditDay: (index: number) => void;
  saving?: boolean;
}

const BookingScheduleTable: React.FC<BookingScheduleTableProps> = ({
  schedule,
  onToggleBooking,
  onEditDay,
  saving = false,
}) => {
  return (
    <div className="bg-white lg:border lg:border-gray-200 lg:rounded-lg overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-100 border-b border-gray-200">
              <th className="text-left text-xs font-semibold text-gray-700 p-3 uppercase tracking-wider">
                Day
              </th>
              <th className="text-left text-xs font-semibold text-gray-700 p-3 uppercase tracking-wider">
                Open Time
              </th>
              <th className="text-left text-xs font-semibold text-gray-700 p-3 uppercase tracking-wider">
                Close Time
              </th>
              <th className="text-left text-xs font-semibold text-gray-700 p-3 uppercase tracking-wider text-center">
                Allow Booking
              </th>
              <th className="text-left text-xs font-semibold text-gray-700 p-3 uppercase tracking-wider text-center">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {schedule.map((day, index) => (
              <tr
                key={day.day}
                className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
              >
                <td className="p-3 text-sm text-gray-900 font-medium">
                  {day.day}
                </td>
                <td className="p-3 text-sm text-gray-700">{day.openTime}</td>
                <td className="p-3 text-sm text-gray-700">{day.closeTime}</td>
                <td className="p-3 text-center">
                  <label className="relative inline-flex items-center cursor-pointer justify-center">
                    <input
                      type="checkbox"
                      checked={day.allowBooking}
                      onChange={() => onToggleBooking(index)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purple-500/40 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#885ABB] peer-disabled:opacity-50"></div>
                  </label>
                </td>
                <td className="p-3 text-center">
                  <button
                    onClick={() => onEditDay(index)}
                    className="bg-[#885ABB] hover:bg-[#7748A4] text-white p-2 rounded-md transition-colors shadow-sm hover:shadow-md inline-flex items-center justify-center"
                    title={`Edit ${day.day}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-3">
        {schedule.map((day, index) => (
          <div
            key={day.day}
            className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm"
          >
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-50">
              <span className="font-bold text-[#402B69]">{day.day}</span>
              <span
                className={cn(
                  "text-[10px] uppercase font-bold px-2 py-1 rounded-full",
                  day.allowBooking
                    ? "bg-green-50 text-green-600"
                    : "bg-red-50 text-red-600"
                )}
              >
                {day.allowBooking ? "Enabled" : "Disabled"}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="space-y-1">
                <span className="text-[10px] text-gray-400 font-bold uppercase block">
                  Open Time
                </span>
                <span className="text-sm font-semibold text-gray-700">
                  {day.openTime || "--:--"}
                </span>
              </div>
              <div className="space-y-1">
                <span className="text-[10px] text-gray-400 font-bold uppercase block">
                  Close Time
                </span>
                <span className="text-sm font-semibold text-gray-700">
                  {day.closeTime || "--:--"}
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={day.allowBooking}
                    onChange={() => onToggleBooking(index)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purple-500/40 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#885ABB]"></div>
                </label>
                <span className="text-xs font-semibold text-gray-500">
                  Allow Booking
                </span>
              </div>

              <button
                onClick={() => onEditDay(index)}
                className="bg-[#885ABB] hover:bg-[#7748A4] text-white p-2 rounded-lg transition-colors shadow-sm flex items-center gap-2 px-3 h-10"
              >
                <Edit2 className="w-4 h-4" />
                <span className="text-xs font-bold uppercase tracking-wider">
                  Edit
                </span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BookingScheduleTable;
